import axios, { isAxiosError } from "@/axios";
import axiosOriginal from "axios";
import { logger } from "@/services/logger";
import { isBrowser, isDeployed } from "@/utils/platform";
import { universalStorage } from "@/utils/universal-storage";
import { getPublicIP } from "@/features/auth/utils/get-public-ip";

export type SendOTPResponseMessage = "CODE_SENT" | "USER_DOESNT_EXIST";
export interface SendOTPResponse {
  message: SendOTPResponseMessage;
}

export const sendOTP = async (email: string): Promise<SendOTPResponseMessage> => {
  const res = await axios.post<SendOTPResponse>("web_account/auth/send_one_time_password", {
    email,
  });
  return res.data.message;
};

export interface SignupRequest {
  first_name: string;
  last_name: string;
  email: string;
  user_flow?: UserFlow;
  submit_url?: string;
  user_ip?: string;
}

export interface SignupResponse {
  detail: string;
  user_id: string;
  username?: string; // sent when registration was successful
  status_code?: "user_already_exists"; // sent when there's an expected error
}

type bundleUserFlow = {
  name: "purchase_profile";
  data: { bundle_id: string } & Record<string, unknown>;
};

export type UserFlow =
  | bundleUserFlow
  | {
      name: string;
      data: Record<string, unknown>;
    };

type signupOptions = {
  userFlow?: UserFlow;
};

export async function signup(firstName: string, lastName: string, email: string, { userFlow }: signupOptions = {}) {
  const isInIframe = window.location !== window.parent.location;

  const body: SignupRequest = {
    first_name: firstName,
    last_name: lastName,
    email,
    user_flow: userFlow,
    submit_url: isInIframe
      ? universalStorage.get("parent_url") || universalStorage.get("url")
      : universalStorage.get("url"),
    user_ip: await getPublicIP(),
  };
  const res = await axios.post<SignupResponse>(`web_account/auth/register`, body);

  return res.data;
}

export interface LoginErrorResponse {
  detail?: {
    error_type: string;
  };
}
export type OnetimePasswordLoginRequest = {
  email: string;
  onetime_password: string;
};

export async function onetimePasswordLogin(email: string, onetime_password: string) {
  const payload: OnetimePasswordLoginRequest = { email: email, onetime_password: onetime_password };
  try {
    await axios.post(`web_account/auth/one_time_password`, payload);
    return { status: "SUCCESS", errorType: "" };
  } catch (error) {
    let errorType = "";
    if (isAxiosError<LoginErrorResponse>(error)) {
      errorType = error?.response?.data?.detail?.error_type || "";
    }
    return { status: "FAIL", errorType: errorType };
  }
}
export const refreshAuthBrowser = async () => {
  return await axiosOriginal.post(
    `${import.meta.env.PUBLIC_API_BASE_URL}web_account/auth/refresh`,
    {},
    { withCredentials: true }
  );
};

type refreshAuthServerResponse = { access_token: string };

export const refreshAuthServer = async (refresh_token?: string) => {
  const res = await axiosOriginal.post<refreshAuthServerResponse>(
    `${import.meta.env.PUBLIC_API_BASE_URL}web_account/auth/ssr/refresh`,
    {},
    {
      headers: {
        Authorization: `Bearer ${refresh_token}`,
      },
    }
  );

  return res.data;
};

export type AuthByTokenRequest = {
  token: string;
};

export type AuthByTokenResponse = {
  access_token: string;
  refresh_token: string;
};

export async function authByToken(token: string) {
  const body: AuthByTokenRequest = { token };
  const res = await axios.post<AuthByTokenResponse>(`web_account/auth/by_token`, body, {
    headers: {
      "Content-Type": "application/json",
    },
  });

  return res.data;
}

export async function logout() {
  try {
    await axiosOriginal.post(
      `${import.meta.env.PUBLIC_API_BASE_URL}web_account/auth/logout`,
      {},
      { withCredentials: true }
    );
  } catch (e) {
    logger.error("Unable to logout properly", { e });
  } finally {
    if (isBrowser() && isDeployed()) window.location.replace(`/auth?redirect_on_auth=${window.location.href}`);
  }
}
